import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert.js';
import expenseService from '../../services/ExpenseService.js';
import { Receipt } from 'lucide-react';
import { PageLayout, FormLayout, FormField, DetailCard } from '../common/index.js';

const EditExpense = () => {
  const { expenseId } = useParams();
  const [formData, setFormData] = useState({
    category: '',
    month: '',
    year: '',
    expense_date: '',
    amount: '',
    description: '',
    vendor_name: '',
    bill_number: '',
    remarks: '',
    status: '',
    payment_mode: '',
    transaction_id: '',
    paid_to: ''
  });
  
  const [fileAttachment, setFileAttachment] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [existingFile, setExistingFile] = useState(null);
  const [hasExistingFile, setHasExistingFile] = useState(false);
  const [categories, setCategories] = useState([]);
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const paymentModes = ['cash', 'upi', 'bank_transfer', 'cheque', 'card', 'online', 'petty_cash'];
  const statuses = ['pending', 'approved', 'paid', 'rejected', 'cancelled'];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

  useEffect(() => {
    fetchExpenseAndDropdowns();
  }, [expenseId]);

  const fetchExpenseAndDropdowns = async () => {
    try {
      const [expenseRes, categoriesRes, monthsRes] = await Promise.all([
        expenseService.getById(expenseId),
        expenseService.getCategories(),
        expenseService.getMonths()
      ]);
      
      const expenseData = expenseRes.data;
      setFormData({
        category: expenseData.category || '',
        month: expenseData.month || '',
        year: expenseData.year?.toString() || '',
        expense_date: expenseData.expense_date || '',
        amount: expenseData.amount || '',
        description: expenseData.description || '',
        vendor_name: expenseData.vendor_name || '',
        bill_number: expenseData.bill_number || '',
        remarks: expenseData.remarks || '',
        status: expenseData.status || '',
        payment_mode: expenseData.payment_mode || '',
        transaction_id: expenseData.transaction_id || '',
        paid_to: expenseData.paid_to || ''
      });

      setCategories(categoriesRes.data || []);
      setMonths(monthsRes.data || []);
      
      if (expenseData.has_attachment) {
        setHasExistingFile(true);
        setExistingFile(expenseService.getFileAttachment(expenseId));
      }
      
    } catch (err) {
      Alert.error('Failed to load expense data');
      console.error(err);
      navigate('/expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('File must be less than 5MB');
        return;
      }
      setFileAttachment(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = () => {
    setFileAttachment(null);
    setFilePreview(null);
    const fileInput = document.getElementById('file-attachment-input');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await expenseService.update(expenseId, formData, fileAttachment);
      Alert.success('Expense updated successfully!');
      setTimeout(() => navigate('/expenses'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update expense');
    } finally {
      setSubmitting(false);
    }
  };

  const openFileInNewTab = () => {
    if (existingFile) {
      window.open(existingFile, '_blank');
    }
  };

  if (loading) {
    return (
      <PageLayout title="Edit Expense" icon={Receipt}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-sm text-gray-600">Loading expense data...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  const isPaidStatus = formData.status === 'paid';

  return (
    <FormLayout
      title="Edit Expense"
      icon={Receipt}
      onSubmit={handleSubmit}
      submitLabel="Update Expense"
      loading={submitting}
      onCancel={() => navigate('/expenses')}
      maxWidth="max-w-7xl"
    >
      <DetailCard title="Basic Information">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
          <FormField label="Category" required>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="input-clean"
              required
            >
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Month" required>
            <select
              name="month"
              value={formData.month}
              onChange={handleChange}
              className="input-clean"
              required
            >
              <option value="">Select Month</option>
              {months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Year" required>
            <select
              name="year"
              value={formData.year}
              onChange={handleChange}
              className="input-clean"
              required
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Expense Date" required>
            <input
              type="date"
              name="expense_date"
              value={formData.expense_date}
              onChange={handleChange}
              className="input-clean"
              required
            />
          </FormField>

          <FormField label="Amount (₹)" required>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              placeholder="Enter amount"
              min="0"
              step="0.01"
              className="input-clean"
              required
            />
          </FormField>

          <FormField label="Description" required className="sm:col-span-3">
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter expense description"
              rows="1"
              className="input-clean"
              required
            />
          </FormField>
        </div>
      </DetailCard>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <DetailCard title="Vendor Information">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <FormField label="Vendor Name">
              <input
                type="text"
                name="vendor_name"
                value={formData.vendor_name}
                onChange={handleChange}
                placeholder="Enter vendor name"
                className="input-clean"
              />
            </FormField>

            <FormField label="Bill Number">
              <input
                type="text"
                name="bill_number"
                value={formData.bill_number}
                onChange={handleChange}
                placeholder="Enter bill number"
                className="input-clean"
              />
            </FormField>
          </div>
        </DetailCard>

        <DetailCard title="Additional Information">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <FormField label="File Attachment">
              {hasExistingFile && !filePreview && (
                <button
                  type="button"
                  onClick={openFileInNewTab}
                  className="mb-3 px-4 py-2 bg-brand text-white rounded-sm hover:bg-brand transition-colors text-sm font-medium flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View Existing File
                </button>
              )}

              <input
                id="file-attachment-input"
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileChange}
                className="input-clean py-1"
              />
              <p className="text-xs text-gray-600 mt-1">
                {hasExistingFile ? 'Upload new file to replace existing one (Max 5MB)' : 'Accepted formats: JPG, PNG, GIF, WebP, PDF (Max 5MB)'}
              </p>
              
              {filePreview && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">New File Preview:</p>
                  <div className="relative inline-block">
                    <img 
                      src={filePreview} 
                      alt="File Preview" 
                      className="w-32 h-32 sm:w-40 sm:h-40 object-cover border-2 border-gray-300 rounded-md"
                    />
                    <button
                      type="button"
                      onClick={removeFile}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                      title="Remove file"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
            </FormField>

            <FormField label="Remarks">
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                placeholder="Enter any additional remarks"
                rows="1"
                className="input-clean"
              />
            </FormField>
          </div>
        </DetailCard>
      </div>

      <DetailCard title="Status & Payment Details">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
          <FormField label="Status">
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="input-clean"
            >
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </FormField>

          {isPaidStatus && (
            <>
              <FormField label="Payment Mode">
                <select
                  name="payment_mode"
                  value={formData.payment_mode}
                  onChange={handleChange}
                  className="input-clean"
                >
                  <option value="">Select Payment Mode</option>
                  {paymentModes.map((mode) => (
                    <option key={mode} value={mode}>
                      {mode.replace(/_/g, ' ').toUpperCase()}
                    </option>
                  ))}
                </select>
              </FormField>

              <FormField label="Transaction ID">
                <input
                  type="text"
                  name="transaction_id"
                  value={formData.transaction_id}
                  onChange={handleChange}
                  placeholder="Enter transaction ID"
                  className="input-clean"
                />
              </FormField>

              <FormField label="Paid To">
                <input
                  type="text"
                  name="paid_to"
                  value={formData.paid_to}
                  onChange={handleChange}
                  placeholder="Enter payee name"
                  className="input-clean"
                />
              </FormField>
            </>
          )}
        </div>
      </DetailCard>
    </FormLayout>
  );
};

export default EditExpense;
